import {
  AlertLooks,
  ButtonLooks,
  CircleLoader,
  SearchTextfield,
  StandardAlert,
  StandardButton,
  StandardLink,
  StandardText,
  useDebounce
} from '@brandfolder/react';
import classNames from 'classnames';
import { FunctionComponent, HTMLAttributes, useContext, useEffect, useState } from 'react';
import styled from 'styled-components';

import { AssetsContext } from '@context/assets';
import { BrandguideContext } from '@context/brandguide';
import { WhitelabelContext } from '@context/whitelabel';
import { ApiResponseObject } from '@typings/api';
import { Asset } from '@typings/asset';

import { ColorsListItem } from './colors-list-item';

interface ColorsListProps extends HTMLAttributes<HTMLDivElement> {}

const UnstyledColorsList: FunctionComponent<ColorsListProps> = (props) => {
  const { colorAssets, errorColors, fetchColors, loadingColors } = useContext(AssetsContext);
  const { whitelabel } = useContext(WhitelabelContext);
  const { brandfolder, collection } = useContext(BrandguideContext);

  const [initialColors, setInitialColors] = useState<ApiResponseObject<Asset>[]>([]);
  const [searchedColors, setSearchedColors] = useState<ApiResponseObject<Asset>[]>([]);
  const [value, setValue] = useState('');
  const valueDebounced = useDebounce(value, process.env.NODE_ENV === 'test' ? 0 : 300);

  const colors = valueDebounced ? searchedColors : initialColors;

  const searchColors = () => {
    return initialColors.filter((color) =>
      color.attributes.name.toLowerCase().includes(value.toLowerCase())
    );
  };

  const resetColors = () => {
    setSearchedColors([]);
    setValue('');
  };

  const collectionOrBrandfolderSlug = () => {
    return collection ? `${brandfolder?.slug}/${collection.slug}` : `${brandfolder?.slug}`;
  };

  useEffect(() => {
    fetchColors();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setInitialColors(colorAssets);
    resetColors();
  }, [colorAssets]);

  useEffect(() => {
    if (valueDebounced) {
      setSearchedColors(searchColors());
    } else {
      setSearchedColors([]);
    }
  }, [valueDebounced]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      {...props}
      className={classNames({
        'colors-list': true,
        [`${props?.className}`]: !!props?.className
      })}
    >
      {loadingColors ? (
        <CircleLoader label="Loading..." />
      ) : colorAssets.length > 0 ? (
        <>
          <SearchTextfield
            id="search-colors"
            label="Search colors"
            onChange={(e) => setValue(e.target.value)}
            placeholder="Search color name..."
            showLabel={false}
            value={value}
          />
          {colors.length > 0 && (
            <ul className="colors-unordered-list">
              {colors.map((data) => {
                return <ColorsListItem asset={data.attributes} id={data.id} key={data.id} />;
              })}
            </ul>
          )}
          {colors.length === 0 && (
            <div className="colors-no-results" role="alert">
              <StandardText>No results found.</StandardText>
              <StandardButton
                className="text-button"
                look={ButtonLooks.TextTertiary}
                onClick={() => resetColors()}
              >
                Clear search.
              </StandardButton>
            </div>
          )}
        </>
      ) : colorAssets.length === 0 && !errorColors ? (
        <StandardAlert look={AlertLooks.None}>
          <StandardText>
            <strong>Note:</strong>{' '}
            {`There are currently no colors associated with ${
              collection ? collection.name : brandfolder?.name
            }.`}{' '}
            <StandardLink
              href={`${whitelabel.BOULDER_URL}/${collectionOrBrandfolderSlug()}`}
              target="_blank"
            >
              Add some color assets.
            </StandardLink>
          </StandardText>
        </StandardAlert>
      ) : errorColors ? (
        <StandardAlert look={AlertLooks.Error}>
          <StandardText>There was an error loading colors.</StandardText>{' '}
          <StandardText>
            <StandardButton
              className="text-button"
              look={ButtonLooks.TextTertiary}
              onClick={() => {
                location.reload();
              }}
            >
              Reload the page and try again.
            </StandardButton>
          </StandardText>
        </StandardAlert>
      ) : null}
    </div>
  );
};

const StyledColorsList = styled(UnstyledColorsList)`
  flex: 1;
  height: 445px;
  overflow-y: auto;
  padding: 20px;

  .bf-textfield__wrapper {
    margin-bottom: 20px;
  }

  .colors-unordered-list {
    display: grid;
    gap: 18px;
    grid-template-columns: repeat(3, 1fr);
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .colors-no-results {
    align-items: center;
    display: flex;
    justify-content: center;

    .text-button {
      margin-left: 4px;
    }
  }

  .text-button {
    background-color: transparent;
    margin: 0;
    min-height: 0;
    padding: 0;

    &:focus,
    &:hover {
      background-color: transparent;
    }
  }
`;

export const ColorsList: FunctionComponent<ColorsListProps> = (props) => {
  return <StyledColorsList {...props} />;
};
